"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useResolvedDarkMode } from "@/lib/theme/use-resolved-dark";
import {
  capturePromoFromSearch,
  clearStoredPromo,
  patchStoredPromo,
  readStoredPromo,
  type StoredPromo,
} from "@/lib/billing/promo-cookie";
import { PROMO_QUERY_KEY } from "@/lib/billing/promo-link";
import {
  pickPromoModalImage,
  PROMO_MOBILE_MEDIA_QUERY,
  type PromoModalImageSet,
} from "@/lib/billing/promo-modal-images";
import {
  DEFAULT_PROMO_BANNER_STYLE,
  PromoBannerModal,
  type PromoBannerModalStyle,
} from "@/components/promo/promo-banner-modal";
import { PromoGlassModal } from "@/components/promo/promo-glass-modal";
import { promoScheduleCopy } from "@/lib/billing/promo-schedule-copy";

type PromoPublic = {
  code: string;
  name: string;
  description: string | null;
  type: "percent_off" | "fixed_off" | "free_credits" | "journey";
  isDemo: boolean;
  /** false — лимит / не начался / истёк */
  available?: boolean;
  status?: "ok" | "not_started" | "expired" | "limit";
  startsAt?: string | null;
  expiresAt?: string | null;
  benefitLabel: string;
  successCtaLabel?: string;
  successPath?: "create_report" | "billing";
  images: PromoModalImageSet;
  style?: PromoBannerModalStyle;
};

type ModalKind = "offer" | "success" | null;

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(PROMO_MOBILE_MEDIA_QUERY);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return isMobile;
}

async function fetchSessionUserId(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/session", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { user?: { id?: string } } | null;
    return data?.user?.id ?? null;
  } catch {
    return null;
  }
}

export function PromoOfferHost() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isDark = useResolvedDarkMode();
  const isMobile = useIsMobileViewport();

  const [stored, setStored] = useState<StoredPromo | null>(null);
  const [promo, setPromo] = useState<PromoPublic | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const autoClaimKey = useRef<string | null>(null);

  const isHome = pathname === "/";
  const isAuthed = Boolean(userId);

  const refreshStored = useCallback(() => {
    setStored(readStoredPromo());
  }, []);

  useEffect(() => {
    void fetchSessionUserId().then(setUserId);
  }, [pathname]);

  useEffect(() => {
    const search = searchParams.toString();
    const onAuthPage =
      pathname === "/login" ||
      pathname === "/register" ||
      pathname.startsWith("/login/") ||
      pathname.startsWith("/register/");
    // На auth-страницах фазу ставит PromoAttachedBanner (pending_auth),
    // иначе capture сбросит её в offer и автоактивация после входа не сработает.
    if (search && searchParams.get(PROMO_QUERY_KEY) && !onAuthPage) {
      capturePromoFromSearch(`?${search}`);
    }
    refreshStored();
  }, [pathname, searchParams, refreshStored]);

  useEffect(() => {
    const code = stored?.code;
    if (!code || stored.phase === "done") {
      setPromo(null);
      return;
    }
    let cancelled = false;
    void fetch(`/api/public/promo/${encodeURIComponent(code)}`, {
      cache: "no-store",
    })
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) {
            setPromo(null);
            // Только «не найден» — чистим; иначе оставляем код
            const body = (await res.json().catch(() => null)) as {
              error?: string;
            } | null;
            if (
              res.status === 404 &&
              (body?.error ?? "").toLowerCase().includes("не найден")
            ) {
              clearStoredPromo();
            }
          }
          return;
        }
        const data = (await res.json()) as PromoPublic;
        if (!cancelled) {
          setPromo({
            ...data,
            images: {
              offerLight: data.images?.offerLight ?? null,
              offerDark: data.images?.offerDark ?? null,
              offerLightMobile: data.images?.offerLightMobile ?? null,
              offerDarkMobile: data.images?.offerDarkMobile ?? null,
              successLight: data.images?.successLight ?? null,
              successDark: data.images?.successDark ?? null,
              successLightMobile: data.images?.successLightMobile ?? null,
              successDarkMobile: data.images?.successDarkMobile ?? null,
            },
            style: {
              ...DEFAULT_PROMO_BANNER_STYLE,
              ...data.style,
            },
            successCtaLabel: data.successCtaLabel,
            successPath: data.successPath,
            status: data.status ?? (data.available === false ? "limit" : "ok"),
            startsAt: data.startsAt,
            expiresAt: data.expiresAt,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setPromo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [stored?.code, stored?.phase]);

  const claimPromo = useCallback(async () => {
    if (!promo || busy) return;
    setBusy(true);
    setError(null);

    try {
      const uid = userId === undefined ? await fetchSessionUserId() : userId;
      if (userId === undefined) setUserId(uid);

      // Гость: регистрация (там же ссылка «Войти»). Промо ждёт в cookie → автоактивация.
      if (!uid) {
        patchStoredPromo({ phase: "pending_auth" });
        refreshStored();
        setModal(null);
        router.push(
          `/register?${PROMO_QUERY_KEY}=${encodeURIComponent(promo.code)}`,
        );
        return;
      }

      // Уже в кабинете:
      // free_credits → redeem; скидка → закрепить в БД.
      if (promo.available === false || promo.status === "not_started" || promo.status === "expired" || promo.status === "limit") {
        const schedule = promoScheduleCopy({
          status: promo.status ?? "limit",
          startsAt: promo.startsAt,
          expiresAt: promo.expiresAt,
          benefitLabel: promo.benefitLabel,
          kind: promo.type === "free_credits" || promo.type === "journey" ? "credits" : "discount",
        });
        setError(schedule.description);
        autoClaimKey.current = null;
        setModal("offer");
        return;
      }

      if (promo.type === "free_credits" || promo.type === "journey") {
        const res = await fetch("/api/billing/coupons/redeem", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: promo.code }),
        });
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (!res.ok) {
          const msg = (body?.error ?? "").toLowerCase();
          const already =
            msg.includes("уже") || msg.includes("использовали");
          if (!already) {
            setError(body?.error ?? "Не удалось применить промокод");
            autoClaimKey.current = null;
            if (isHome) setModal("offer");
            return;
          }
        }
      } else {
        // Скидка: закрепляем за аккаунтом в БД (+ sessionStorage на всякий случай).
        const res = await fetch("/api/billing/coupons/redeem", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: promo.code }),
        });
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (!res.ok) {
          const msg = (body?.error ?? "").toLowerCase();
          const already =
            msg.includes("уже") ||
            msg.includes("использовали") ||
            msg.includes("закреп");
          if (!already) {
            setError(body?.error ?? "Не удалось закрепить скидку");
            autoClaimKey.current = null;
            if (isHome) setModal("offer");
            return;
          }
        }
        try {
          sessionStorage.setItem(
            "idealitika:pending_checkout_coupon",
            promo.code,
          );
        } catch {
          /* ignore */
        }
      }

      patchStoredPromo({ phase: "success", offerDismissed: true });
      refreshStored();
      setModal("success");
    } catch {
      setError("Не удалось применить промокод");
      autoClaimKey.current = null;
    } finally {
      setBusy(false);
    }
  }, [promo, busy, userId, refreshStored, router, isHome]);

  // Автоактивация только после register/login (pending_auth).
  // Если уже в кабинете и открыли ?promo= — показываем оффер, пользователь жмёт CTA.
  useEffect(() => {
    if (userId === undefined) return;
    if (!isAuthed || !stored || !promo) return;
    if (stored.phase !== "pending_auth") return;
    if (promo.available === false) return;
    if (busy) return;
    const key = `${stored.code}:${userId}`;
    if (autoClaimKey.current === key) return;
    autoClaimKey.current = key;
    void claimPromo();
  }, [isAuthed, stored, promo, busy, userId, claimPromo]);

  // Оффер — на главной (гость и авторизованный); success — везде.
  useEffect(() => {
    if (!stored || !promo) {
      setModal(null);
      return;
    }

    if (stored.phase === "success") {
      setModal("success");
      return;
    }

    // После pending_auth идёт auto-claim — не мешаем оффером.
    if (isAuthed && stored.phase === "pending_auth" && promo.available !== false) {
      setModal(null);
      return;
    }

    if (
      isHome &&
      (stored.phase === "offer" || stored.phase === "pending_auth") &&
      !stored.offerDismissed
    ) {
      setModal("offer");
      return;
    }

    setModal(null);
  }, [isHome, stored, promo, isAuthed]);

  function dismissOffer(open: boolean) {
    if (open) return;
    if (modal === "offer") {
      patchStoredPromo({ offerDismissed: true });
      refreshStored();
    }
    if (modal === "success") {
      patchStoredPromo({ phase: "done" });
      refreshStored();
    }
    setModal(null);
  }

  function goToNextStep() {
    patchStoredPromo({ phase: "done" });
    refreshStored();
    setModal(null);
    const successPath =
      promo?.successPath ??
      (promo?.type === "free_credits" || promo?.type === "journey"
        ? "create_report"
        : "billing");
    if (successPath === "billing") {
      router.push(
        `/billing?coupon=${encodeURIComponent(promo?.code ?? "")}`,
      );
      return;
    }
    router.push(isAuthed ? "/reports/create" : "/login");
  }

  if (!promo || !modal) {
    if (error && isAuthed) {
      return (
        <div className="fixed bottom-4 left-1/2 z-[200] w-[min(100%-1.5rem,28rem)] -translate-x-1/2 rounded-xl border border-[var(--color-border-danger,#fca5a5)] bg-[var(--color-bg-danger,#fef2f2)] px-4 py-3 shadow-lg">
          <p className="text-sm font-semibold text-primary">Промокод не применён</p>
          <p className="mt-1 text-sm text-secondary">{error}</p>
          <button
            type="button"
            className="mt-2 text-sm font-medium text-primary underline"
            onClick={() => {
              setError(null);
              autoClaimKey.current = null;
              void claimPromo();
            }}
          >
            Повторить
          </button>
        </div>
      );
    }
    return null;
  }

  const offerImage = pickPromoModalImage(promo.images, "offer", {
    isDark,
    isMobile,
  });
  const successImage = pickPromoModalImage(promo.images, "success", {
    isDark,
    isMobile,
  });
  const bannerStyle = promo.style ?? DEFAULT_PROMO_BANNER_STYLE;
  const successCta =
    promo.successCtaLabel ??
    (promo.type === "free_credits" || promo.type === "journey"
      ? "Создать отчёт"
      : "Выбрать тариф со скидкой");
  const benefit =
    promo.benefitLabel ||
    (promo.type === "free_credits" || promo.type === "journey" ? "Подарок" : "Скидка");

  const schedule = promoScheduleCopy({
    status: promo.status ?? (promo.available === false ? "limit" : "ok"),
    startsAt: promo.startsAt,
    expiresAt: promo.expiresAt,
    benefitLabel: benefit,
    kind: promo.type === "free_credits" || promo.type === "journey" ? "credits" : "discount",
  });

  const offerTitle = schedule.isUnavailable
    ? schedule.title
    : promo.type === "free_credits" || promo.type === "journey"
      ? "Поздравляем — вам подарок!"
      : "Поздравляем — вам скидка!";
  const offerDescription = schedule.isUnavailable
    ? schedule.description
    : isAuthed
      ? promo.type === "free_credits" || promo.type === "journey"
        ? "Нажмите кнопку — кредиты сразу появятся на счёте."
        : "Нажмите кнопку — скидка закрепится за аккаунтом и будет видна в кабинете."
      : "Войдите или зарегистрируйтесь — промо применится автоматически.";
  const offerCta = schedule.isUnavailable
    ? schedule.ctaLabel
    : !isAuthed
      ? "Получить"
      : promo.type === "free_credits" || promo.type === "journey"
        ? "Забрать подарок"
        : "Закрепить скидку";

  function onOfferPrimary() {
    if (schedule.isUnavailable) {
      dismissOffer(false);
      return;
    }
    void claimPromo();
  }

  if (modal === "offer") {
    if (offerImage && !schedule.isUnavailable) {
      return (
        <PromoBannerModal
          imageUrl={offerImage}
          isMobile={isMobile}
          style={bannerStyle}
          busy={busy}
          ariaLabel={offerCta}
          error={error}
          onPrimary={onOfferPrimary}
          onClose={() => dismissOffer(false)}
        />
      );
    }
    return (
      <PromoGlassModal
        variant={schedule.isUnavailable ? "info" : "offer"}
        title={offerTitle}
        benefitLabel={benefit}
        description={offerDescription}
        ctaLabel={offerCta}
        busy={busy}
        error={schedule.isUnavailable ? null : error}
        onPrimary={onOfferPrimary}
        onClose={() => dismissOffer(false)}
      />
    );
  }

  if (successImage) {
    return (
      <PromoBannerModal
        imageUrl={successImage}
        isMobile={isMobile}
        style={bannerStyle}
        ariaLabel={successCta}
        onPrimary={goToNextStep}
        onClose={() => dismissOffer(false)}
      />
    );
  }

  const successTitle =
    promo.type === "free_credits" || promo.type === "journey"
      ? "Поздравляем! Подарок получен"
      : "Поздравляем! Скидка ваша";
  const successDescription =
    promo.type === "free_credits" || promo.type === "journey"
      ? "Кредиты уже на счёте. Можно сразу создать отчёт."
      : "Скидка закреплена за аккаунтом — вы увидите её в кабинете и на странице тарифов при оплате.";

  return (
    <PromoGlassModal
      variant="success"
      title={successTitle}
      benefitLabel={benefit}
      description={successDescription}
      ctaLabel={
        promo.type === "free_credits" || promo.type === "journey"
          ? successCta
          : "Перейти в кабинет / к тарифам"
      }
      onPrimary={() => {
        if (promo.type === "free_credits" || promo.type === "journey") {
          goToNextStep();
          return;
        }
        patchStoredPromo({ phase: "done" });
        refreshStored();
        setModal(null);
        router.push(
          `/billing?coupon=${encodeURIComponent(promo.code)}`,
        );
      }}
      onClose={() => dismissOffer(false)}
    />
  );
}
